export type LabResult = {
  name: string;
  value: string;
  unit: string;
  abnormal?: boolean;
};

export type WardPatient = {
  id: string;
  name: string;
  englishName: string;
  hn: string;
  an: string;
  age: number;
  ward: string;
  bed: string;
  diagnosis: string;
  status: "Stable" | "Watch" | "Critical";
  updated: string;
};

export const patient = {
  id: "pt-athena",
  hn: "HN 54001231",
  an: "680000007",
  name: "นางอาเทน่า ทดสอบ",
  englishName: "Mrs. Athena Test",
  age: 62,
  sex: "Female",
  ward: "Medicine 2",
  bed: "M2-08",
  admittedDay: 4,
  allergies: ["Penicillin — rash"],
  coverage: "Civil Servant Medical Benefit",
  diagnoses: [
    { code: "I10", shortLabel: "HT", label: "Essential hypertension" },
    { code: "E11.9", shortLabel: "T2DM", label: "Type 2 diabetes mellitus" },
    { code: "D64.9", shortLabel: "Anemia", label: "Anemia, unspecified" },
  ],
  vitals: {
    bp: "118/72",
    spo2: "97%",
    pulse: "82",
    temp: "37.1°C",
    series: [74, 78, 83, 80, 84, 82, 81, 82],
  },
  labs: {
    CBC: [
      { name: "Hb", value: "7.2", unit: "g/dL", abnormal: true },
      { name: "Hct", value: "23.1", unit: "%", abnormal: true },
      { name: "WBC", value: "7.8", unit: "×10³/µL" },
      { name: "Platelet", value: "242", unit: "×10³/µL" },
    ] satisfies LabResult[],
    Chem7: [
      { name: "Na", value: "138", unit: "mmol/L" },
      { name: "K", value: "4.1", unit: "mmol/L" },
      { name: "Cr", value: "1.3", unit: "mg/dL", abnormal: true },
      { name: "Mg", value: "1.6", unit: "mg/dL", abnormal: true },
    ] satisfies LabResult[],
  },
  history: [
    "Hypertension for 12 years; home BP usually 130–140/80 mmHg.",
    "Type 2 diabetes on metformin; last HbA1c 7.4%.",
    "No prior transfusion reaction documented.",
  ],
  priorNotes: [
    {
      at: "23 Jul · 16:40",
      author: "Dr. Kittisak",
      text: "Afebrile, tolerating diet. Anemia under evaluation. Repeat CBC in AM.",
    },
    {
      at: "23 Jul · 08:10",
      author: "Dr. Heartbeat",
      text: "Fatigue improved after rest. No melena or hematemesis reported.",
    },
  ],
  mar: [
    "Metformin 500 mg PO bid pc — held for renal review",
    "Amlodipine 5 mg PO od — given 08:00",
    "Warfarin 2 mg PO hs — active",
    "Amoxicillin 500 mg PO tid — pending verification",
  ],
  imaging: [
    {
      id: "cxr-2407",
      kind: "image",
      title: "CXR AP portable",
      subtitle: "PACS · Today 07:42",
      status: "Final report",
      summary: "Mild cardiomegaly. No focal air-space opacity or pleural effusion.",
      author: "Dr. R. Wattanakul, Radiology",
    },
    {
      id: "ecg-2407",
      kind: "waveform",
      title: "12-lead ECG",
      subtitle: "Cardiology archive · Today 07:35",
      status: "Reviewed",
      summary: "Sinus rhythm 78 bpm. PR interval 218 ms, slightly prolonged from baseline.",
      author: "Auto-analysis; clinician review required",
    },
    {
      id: "scan-referral",
      kind: "document",
      title: "Referral note — scanned.pdf",
      subtitle: "Scanned document · 22 Jul 14:18",
      status: "2 pages",
      summary: "Outside-hospital medication history and previous anemia work-up.",
      author: "Imported by MR staff",
    },
  ],
};

export const wardPatients: WardPatient[] = [
  {
    id: patient.id,
    name: patient.name,
    englishName: patient.englishName,
    hn: "54001231",
    an: patient.an,
    age: patient.age,
    ward: patient.ward,
    bed: patient.bed,
    diagnosis: "Anemia / T2DM / HT",
    status: "Watch",
    updated: "2 min ago",
  },
  {
    id: "pt-02",
    name: "นายสมชาย ใจดี",
    englishName: "Mr. Somchai Jaidee",
    hn: "52018042",
    an: "680000011",
    age: 71,
    ward: "Medicine 2",
    bed: "M2-03",
    diagnosis: "CHF exacerbation",
    status: "Stable",
    updated: "9 min ago",
  },
  {
    id: "pt-03",
    name: "นางสาวพิมพ์ชนก แสงดาว",
    englishName: "Ms. Pimchanok Saengdao",
    hn: "61088216",
    an: "680000014",
    age: 48,
    ward: "Medicine 2",
    bed: "M2-05",
    diagnosis: "Community-acquired pneumonia",
    status: "Watch",
    updated: "14 min ago",
  },
  {
    id: "pt-04",
    name: "นายณัฐพงษ์ สุขใจ",
    englishName: "Mr. Nattapong Sukjai",
    hn: "58006119",
    an: "680000019",
    age: 56,
    ward: "Surgical 1",
    bed: "S1-12",
    diagnosis: "Post-op appendectomy D1",
    status: "Stable",
    updated: "18 min ago",
  },
  {
    id: "pt-05",
    name: "นางกนกวรรณ ดีพร้อม",
    englishName: "Mrs. Kanokwan Deeprom",
    hn: "49010701",
    an: "680000021",
    age: 76,
    ward: "Medicine 2",
    bed: "M2-11",
    diagnosis: "Sepsis pathway",
    status: "Critical",
    updated: "Now",
  },
  {
    id: "pt-06",
    name: "นายธันวา มั่นคง",
    englishName: "Mr. Thanwa Munkong",
    hn: "63003482",
    an: "680000026",
    age: 39,
    ward: "Medicine 2",
    bed: "M2-16",
    diagnosis: "Dengue fever",
    status: "Stable",
    updated: "23 min ago",
  },
];
